import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";

import { shortMonthAndShortYear, shortMonthAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes, colorsSets } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignOnlyFilter } from "app/filters/ngFilters";
import { getIntervalConfig } from "app/pages/website-analysis/traffic-sources/display-ads/overview/graph/helpers/DisplayAdsGraphDataParsers";
import {
    deviceTypeToTextMap,
    timeGranularityObjects,
} from "app/pages/website-analysis/traffic-sources/search/constants/paidSearchGraphConstants";
import type { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import watermark from "pro-features/components/Chart/src/configs/watermark/watermark";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisGridLineConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisGridLineConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import noZoomConfig from "pro-features/components/Chart/src/configs/zoom/noZoomConfig";
import type { ValueOf } from "pro-features/types";

type Row = Record<ValueOf<typeof DEVICES_TYPES_FOR_API>, number>;

const colorSet = colorsSets.b1.toArray();
const dateFormat = (date) => dayjs(date).format(shortMonthAndYear);

export const getChartConfig = (seriesData: Record<string, any>) => {
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    const yAxisFormatter = ({ value }) => abbrNumberFilter()(value, 0);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(shortMonthAndShortYear);

    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        noLegendConfig,
        xAxisLabelsConfig,
        xAxisCrosshair,
        yAxisLabelsConfig,
        yAxisGridLineConfig,
        tooltipContainerConfig,
        noZoomConfig,
        watermark,
        getIntervalConfig(timeGranularityObjects.monthly.name),
        {
            tooltip: {
                shared: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    const { index } = this.points[0].point;
                    const data = Object.entries(seriesData).reduce((agr, [key, series]) => {
                        return {
                            ...agr,
                            [key]: series.data?.[index]?.[1],
                        };
                    }, {} as Row);

                    return ReactDOMServer.renderToString(
                        <ChartTooltip
                            data={getRows(data)}
                            columnDefinition={SEARCH_VOLUME_COLUMN_DEFINITION}
                            width={325}
                            title={translate("keywordresearch.monitorkeywords.over.time.graph.tooltip.title")}
                            subtitle={dateFormat(this.x)}
                        >
                            <ChartTooltip.TooltipFooterWithValue
                                label={
                                    <ChartTooltip.LabelCell
                                        label={deviceTypeToTextMap.Total}
                                        backgroundColor={colorsPalettes.midnight[50]}
                                    />
                                }
                                value={abbrNumberFilter()(data.Total)}
                            />
                        </ChartTooltip>,
                    );
                },
            },
        },
        {
            chart: {
                height: 226,
                spacingTop: 10,
                marginTop: 27,
                marginBottom: 27,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
            },
            plotOptions: {
                series: {
                    fillOpacity: 1,
                    animation: false,
                },
            },
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: colorsPalettes.carbon[250],
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                type: "datetime",
            },
            yAxis: {
                showFirstLabel: true,
                showLastLabel: true,
                labels: {
                    formatter: yAxisFormatter,
                },
            },
        },
    ]);
};

const getRows = (rowData: Row) => {
    const mobileShare = (rowData.Mobile / rowData.Total) * 100;
    return [
        {
            device: {
                label: deviceTypeToTextMap.MobileWeb,
                backgroundColor: colorSet[0],
            },
            share: {
                value: mobileShare,
            },
            volume: {
                value: rowData.Mobile,
            },
        },
        {
            device: {
                label: deviceTypeToTextMap.Desktop,
                backgroundColor: colorSet[1],
            },
            share: {
                value: 100 - mobileShare,
            },
            volume: {
                value: rowData.Desktop,
            },
        },
    ];
};

interface TooltipRow {
    device: { label: string; backgroundColor: string };
    share: { value: number };
    volume: { value: number };
}

const SEARCH_VOLUME_COLUMN_DEFINITION: Array<ColumnDefinition<TooltipRow>> = [
    {
        key: "1",
        get title() {
            return translate("keywordresearch.monitorkeywords.over.time.graph.tooltip.device");
        },
        selector: "device" as const,
        align: "left",
        size: ChartTooltip.LabelCellDefaultWidth,
        render: ({ device }) => {
            return (
                <ChartTooltip.LabelCell
                    label={device.label}
                    backgroundColor={device.backgroundColor}
                />
            );
        },
    },
    {
        key: "2",
        get title() {
            return translate("keywordresearch.monitorkeywords.over.time.graph.tooltip.share.of.volume");
        },
        selector: "share" as const,
        align: "right",
        size: ChartTooltip.ChangeCellDefaultWidth,
        borderRight: true,
        render: ({ share }) => {
            return <ChartTooltip.ValueCell value={percentageSignOnlyFilter()(share.value, 0)} />;
        },
    },
    {
        key: "3",
        get title() {
            return translate("keywordresearch.monitorkeywords.over.time.graph.tooltip.avg.volume");
        },
        selector: "volume" as const,
        align: "right",
        size: "60px",
        render: ({ volume }) => {
            // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
            return <ChartTooltip.ValueCell value={abbrNumberFilter()(volume.value, 0)} />;
        },
    },
];
